import { Card } from "@/components/ui/card"
import { useEffect, useRef } from "react"

export function PathwaySection() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return
      const images = sectionRef.current.querySelectorAll<HTMLDivElement>('[data-parallax]')
      
      images.forEach((img) => {
        const rect = img.getBoundingClientRect()
        const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
        const clampedProgress = Math.max(0, Math.min(1, scrollProgress))
        const translateY = (clampedProgress - 0.5) * -30
        img.style.transform = `translateY(${translateY}px) scale(1.1)`
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  const pathways = [
    {
      age: "Ages 6–9",
      phase: "Grassroots Development",
      focus: "Ball mastery, coordination, love for the game",
      image: "/images/grassroots-development.jpg",
    },
    {
      age: "Ages 10–12",
      phase: "Foundation Phase",
      focus: "Technical excellence and position awareness",
      image: "/images/foundation-phase.jpg",
    },
    {
      age: "Ages 13–15",
      phase: "Youth Elite Phase",
      focus: "Tactical intelligence and mental toughness",
      image: "/images/youth-elite-phase.jpg",
    },
    {
      age: "Ages 16–18",
      phase: "Professional Preparation",
      focus: "Match intelligence, professionalism, exposure",
      image: "/images/professional-preparation.jpg",
    },
    {
      age: "Ages 18+",
      phase: "Professional Transition",
      focus: "Club placements, scholarships, career management",
      image: "/images/professional-transition.jpg",
    },
  ]

  return (
    <section id="programs" ref={sectionRef} className="py-20 bg-navy text-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 text-gold text-sm font-medium uppercase tracking-wider mb-6">
            ⚽ Football Development Pathway
          </div>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6">
            The ZOE Football Development Pathway
          </h2>
          <p className="text-xl text-ivory/70 max-w-2xl mx-auto">
            A structured football journey from grassroots to professional transition.
          </p>
        </div>

        <div className="space-y-4">
          {pathways.map((pathway, index) => (
            <div key={index}>
              <Card className="overflow-hidden bg-navy-muted border border-ivory/10 hover:border-gold/50 transition-all duration-500 text-ivory group p-0">
                <div className="flex flex-col lg:flex-row">
                  {/* Content Side */}
                  <div className="p-6 lg:p-8 lg:w-2/5 flex flex-col justify-center">
                    <div className="text-gold font-semibold text-sm uppercase tracking-wider mb-2">
                      {pathway.age}
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-semibold mb-2 text-ivory">
                      {pathway.phase}
                    </h3>
                    <p className="text-ivory/70 text-lg leading-relaxed">
                      {pathway.focus}
                    </p>
                  </div>
                  
                  {/* Image Side with Scroll Parallax - extends to edges */}
                  <div className="lg:w-3/5 h-48 lg:h-auto min-h-[180px] relative overflow-hidden">
                    <div 
                      data-parallax
                      className="absolute -inset-y-8 inset-x-0 bg-cover bg-top"
                      style={{ 
                        backgroundImage: `url(${pathway.image})`,
                      }}
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-navy-muted via-navy-muted/30 to-transparent" />
                  </div>
                </div>
              </Card>
              
              {index < pathways.length - 1 && (
                <div className="flex justify-center py-2">
                  <div className="w-0.5 h-4 bg-gradient-to-b from-gold/50 to-gold/10" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
